import type { TranslationResource } from './sr-Latn.ts';

export const en: TranslationResource = {
  app: { name: 'SprintDeck', tagline: 'Presentations whose flow the audience chooses' },
  common: {
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    close: 'Close',
    delete: 'Delete',
    archive: 'Archive',
    edit: 'Edit',
    create: 'Create',
    add: 'Add',
    remove: 'Remove',
    back: 'Back',
    retry: 'Retry',
    loading: 'Loading…',
    search: 'Search',
    minutes: 'min',
    seconds: 's',
    optional: 'optional',
    none: 'None',
    confirm: 'Confirm',
    copy: 'Copy',
    open: 'Open',
    slides_one: '{{count}} slide',
    slides_few: '{{count}} slides',
    slides_other: '{{count}} slides',
  },
  nav: {
    presentations: 'Presentations',
    topics: 'Sections',
    assets: 'Files',
    account: 'Account',
    logout: 'Log out',
    theme: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
    language: 'Language',
    mainNavigation: 'Main navigation',
    sessions: 'Talks',
    users: 'Users',
  },
  auth: {
    loginTitle: 'Welcome back',
    loginSubtitle: 'Sign in to manage your presentations.',
    registerTitle: 'Create a presenter account',
    registerSubtitle: 'An administrator approves new accounts. Your audience does not need one.',
    pendingTitle: 'Request sent',
    pendingBody:
      'An administrator needs to approve your account. Once approved, sign in with the email and password you entered.',
    email: 'Email address',
    password: 'Password',
    passwordHint: 'At least 10 characters.',
    displayName: 'Full name',
    login: 'Sign in',
    register: 'Create account',
    noAccount: 'No account yet?',
    haveAccount: 'Already have an account?',
    goRegister: 'Register',
    goLogin: 'Sign in',
    heroTitle: 'The audience picks the Sprint. You run the talk.',
    heroBody:
      'Sections form the Product Backlog, each poll is Sprint Planning, and the timer keeps the timebox. Participants vote from their phones without signing up.',
    heroPoint1: 'Import PDF and PowerPoint slides',
    heroPoint2: 'Live voting through a QR code',
    heroCardIdea: 'Idea',
    heroCardTeam: 'Team',
    heroCardMarket: 'Market',
    heroCardMoney: 'Money',
    heroPoint3: 'Animated forward and rewind transitions',
  },
  account: {
    title: 'Account',
    subtitle: 'Personal details, interface language and password.',
    profile: 'Profile',
    locale: 'Interface language',
    changePassword: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    passwordChanged: 'Password changed. Other sessions were signed out.',
    saved: 'Saved.',
  },
  errors: {
    NOTHING_TO_UNDO: 'There is no change to undo.',
    NOTHING_TO_REDO: 'There is no change to redo.',
    ASSET_NOT_FAILED: 'Only a file whose conversion failed can be converted again.',
    COLLECTION_NAME_TAKEN: 'An area with this name already exists.',
    DYNAMIC_POLL_REQUIRED: 'The dynamic poll cannot be deleted. You can switch it off in the Polls tab.',
    INTRO_REQUIRED: 'The intro cannot be removed from the presentation.',
    JOIN_CODE_TAKEN: 'Another talk already uses this code.',
    NO_LIBRARY_TOPIC: 'This section has no source section in the library.',
    NOT_PUBLISHED: 'Save the presentation first, then start the talk.',
    PAIRING_EXPIRED: 'The pairing code expired or was already used. Ask for a new one in the console.',
    SESSION_ACTIVE: 'The talk is running. End it, then delete the report.',
    SESSION_FULL: 'The talk has reached its participant limit.',
    TOPIC_ALREADY_ADDED: 'This section is already in the presentation.',
    TOPIC_ARCHIVED: 'An archived section cannot be added. Restore it from the archive first.',
    TOPIC_IN_USE:
      'The section is used in a presentation, so it cannot be deleted. Archive it or remove it from the presentations first.',
    UNKNOWN_COLLECTION: 'The area does not exist or was deleted.',
    PRESENTATION_LIVE: 'A talk of this presentation is running. End it, then delete the presentation.',
    ASSET_IN_USE: 'Slides from this file are in use, so the file cannot be deleted.',
    ASSET_PROCESSING: 'Wait until the conversion finishes.',
    INVALID_CREDENTIALS: 'Wrong email or password.',
    EMAIL_TAKEN: 'An account with this email already exists.',
    RATE_LIMITED: 'Too many attempts. Wait a minute and try again.',
    VALIDATION_FAILED: 'Please check the entered data.',
    WRONG_PASSWORD: 'The current password is not correct.',
    REGISTRATION_CLOSED: 'Registration is currently disabled.',
    SURVEY_PLACEMENT_INVALID: 'The chosen slide is not in that section.',
    SURVEY_PLACEMENT_TAKEN: 'Another survey already follows this slide.',
    SURVEY_CLOSED: 'The survey is closed.',
    UNKNOWN_SURVEY: 'The survey was not found.',
    INVALID_ANSWER: 'The answer is not valid.',
    ANSWER_TOO_LONG: 'The answer is too long.',
    EMPTY_ANSWER: 'Type or choose an answer.',
    TOO_MANY_OPTIONS: 'Too many answers are selected.',
    UNKNOWN_OPTION: 'The option no longer exists.',
    TOO_MANY_SURVEYS: 'A presentation can hold at most 30 surveys.',
    SESSION_COMPLETED: 'The talk has ended.',
    ACCOUNT_PENDING: 'Your account is still waiting for administrator approval.',
    ACCOUNT_DISABLED: 'This account has been disabled. Contact an administrator.',
    ADMIN_ONLY: 'Only administrators can do this.',
    LAST_ADMIN: 'At least one active administrator must remain.',
    CANNOT_CHANGE_SELF: 'You cannot do this to your own account.',
    USER_NOT_APPROVED: 'Approve the account first.',
    USER_ALREADY_APPROVED: 'This account is already approved.',
    USER_HAS_LIVE_SESSION: 'This user is presenting right now. Try again when the talk ends.',
    CONFIRM_EMAIL_MISMATCH: 'The email does not match this account.',
    NETWORK: 'The server is not reachable. Check your connection.',
    VERSION_CONFLICT: 'Someone changed this in the meantime. The latest version is shown.',
    PAYLOAD_TOO_LARGE: 'The file is too large.',
    NOT_FOUND: 'The requested item does not exist.',
    generic: 'Something went wrong. Please try again.',
  },
  assets: {
    title: 'Files',
    subtitle: 'PDF and PowerPoint documents you pick slides from.',
    upload: 'Upload file',
    dropTitle: 'Drop a PDF, or a PPTX together with its PDF',
    dropBody: 'or click to choose files. Up to {{size}} MB per file.',
    uploading: 'Uploading {{percent}}%',
    emptyTitle: 'No files yet',
    emptyBody: 'Upload your first deck. Every page becomes a slide you can reuse in many sections.',
    status: { QUEUED: 'Queued', PROCESSING: 'Processing', READY: 'Ready', FAILED: 'Failed' },
    processingPages: 'Pages processed: {{count}}',
    usedIn_one: 'Used in {{count}} section',
    usedIn_few: 'Used in {{count}} sections',
    usedIn_other: 'Used in {{count}} sections',
    notUsed: 'Not used',
    showArchived: 'Show archived',
    archived: 'Archived',
    unarchive: 'Restore',
    deleteTitle: 'Delete file?',
    deleteBody: 'The file and its slides will be deleted permanently.',
    deleted: 'File deleted.',
    inUseTitle: 'The file is in use',
    inUseBody:
      'Slides from this file are in use, so the file cannot be deleted yet. Remove the slides from these places or archive the file: an archived file is no longer offered when picking slides, and presentations that use it keep working.',
    inUseSavedVersions:
      'Saved versions keep their slides for reports and for running the talk again. The file is freed only when those presentations are deleted permanently.',
    checkingUsage: 'Checking where the file is used…',
    archivedToast: 'The file was archived.',
    usage: {
      topics: 'Sections',
      presentations: 'Presentations',
      savedVersions: 'Saved versions of presentations',
    },
    chooseFiles: 'Choose files',
    onePowerPointAtATime: 'Send one PowerPoint file at a time, together with its PDF.',
    pdfNeededTitle: 'The PDF is needed too',
    pdfNeededBody:
      'In PowerPoint open the same file and choose File > Export > PDF, then drop or choose that PDF here. It must have as many pages as the PowerPoint file has visible slides. Slides are shown from the PDF and the notes come from the PowerPoint file.',
    choosePdf: 'Choose the PDF',
    uploaded: 'File received. Conversion is running.',
    limitationsTitle: 'PowerPoint files',
    limitationsBody:
      'Send a PowerPoint file together with the PDF exported from PowerPoint (File > Export > PDF). Slides are shown from the PDF, with your fonts, and only the notes are taken from the PPTX; the PPTX is not stored. Animations, transitions, video, audio and interactive elements are not shown, each slide is an image.',
    errors: {
      PDF_REQUIRED: 'Send the PDF exported from PowerPoint together with the PowerPoint file.',
      PAIR_INVALID: 'Send one PowerPoint file and its PDF.',
      PAGE_COUNT_MISMATCH:
        'The PDF has {{pages}} pages but the PowerPoint file has {{slides}} visible slides. Export the PDF again from the same file, without hidden slides and without notes pages.',
      UNSUPPORTED_TYPE: 'Only PDF and PPTX files are supported.',
      MACRO_ENABLED: 'The file contains macros. Save it as a plain .pptx.',
      ENCRYPTED: 'The file is password protected or uses a legacy format.',
      ZIP_BOMB: 'The file expands to an unsafe size.',
      CORRUPT: 'The file is damaged.',
      EMPTY_FILE: 'The file is empty.',
      TOO_MANY_PAGES: 'The document has too many pages.',
      TIMEOUT: 'The conversion took too long.',
      CONVERSION_FAILED: 'The conversion failed.',
      INTERNAL: 'Server error during conversion.',
    },
    slides: 'Slides',
  },
  topics: {
    title: 'Sections',
    subtitle: 'Your library of sections. A section can be reused in many presentations.',
    create: 'New section',
    emptyTitle: 'No sections yet',
    emptyBody: 'A section is one Sprint: a title, a short description and a few slides.',
    name: 'Section name',
    description: 'Short description',
    color: 'Colour',
    icon: 'Icon',
    duration: 'Estimated duration',
    durationHint: 'In minutes',
    slides: 'Section slides',
    addSlides: 'Add slides',
    noSlides: 'This section has no slides yet.',
    noSlidesBody: 'Add slides from your uploaded files.',
    usedIn_one: 'In {{count}} presentation',
    usedIn_few: 'In {{count}} presentations',
    usedIn_other: 'In {{count}} presentations',
    dragHint: 'Drag the cards to change the order.',
    saved: 'Section saved.',
    copy: 'Copy',
    copyTitle: '{{title}} (copy)',
    copied: 'The section was copied.',
    deleteTitle: 'Delete section?',
    deleteBody: 'The section will be removed from the library permanently.',
    archiveInsteadBody: 'The section is used in a presentation, so it can only be archived.',
    removeSlide: 'Remove slide',
    moveSlide: 'Move slide',
    back: 'All sections',
  },
  slideNotes: {
    title: 'Notes for slide {{position}} of {{count}}',
    hint: 'Only you see the notes: in the console, on your phone and in the preview. The projector and the audience never do.',
    label: 'Notes',
    placeholder: 'What you want to say with this slide',
    counter: '{{count}} of {{max}} characters',
    previous: 'Previous',
    next: 'Next',
    slide: 'Slide {{position}}',
    open: 'Notes for slide {{position}}',
    openWithNotes: 'Notes for slide {{position}}, has notes',
    saveFailed: 'The notes were not saved. Try again.',
    show: 'Show notes',
    hide: 'Hide notes',
    heading: 'Notes',
    empty: 'This slide has no notes.',
  },
  history: {
    undo: 'Undo the last change ({{shortcut}})',
    redo: 'Redo the undone change ({{shortcut}})',
  },
  picker: {
    scope: 'Which files',
    thisCollection: 'This area',
    allFiles: 'All files',
    title: 'Choose slides',
    titleFor: 'Choose slides for: {{target}}',
    description:
      'Click slides in the order you want them shown. Shift and click picks every slide between two clicks.',
    files: 'Files',
    noReadyFiles: 'No ready files. Upload a PDF or PPTX first.',
    selected_one: '{{count}} slide selected',
    selected_few: '{{count}} slides selected',
    selected_other: '{{count}} slides selected',
    add: 'Add selected',
    selectAll: 'Select all',
    alreadyAdded: 'Already added',
  },
  presentations: {
    title: 'Presentations',
    subtitle: 'Build a backlog of sections, prepare polls and save a version for your talk.',
    create: 'New presentation',
    createTitle: 'New presentation',
    name: 'Title',
    description: 'Description',
    duration: 'Talk duration',
    emptyTitle: 'No presentations yet',
    emptyBody: 'Create a presentation and add sections from your library.',
    draft: 'Draft',
    published: 'Saved version {{version}}',
    unpublished: 'Changes not saved',
    topics_one: '{{count}} section',
    topics_few: '{{count}} sections',
    topics_other: '{{count}} sections',
    polls_one: '{{count}} poll',
    polls_few: '{{count}} polls',
    polls_other: '{{count}} polls',
    minutes: '{{count}} min',
    copy: 'Duplicate',
    copyTitle: '{{title}} (copy)',
    copied: 'Copy created.',
    archiveDone: 'Presentation archived.',
    deleteForever: 'Delete permanently',
    deleteTitle: 'Delete "{{title}}" permanently?',
    deleteBody:
      'The presentation, all its saved versions and the reports of its talks are deleted. This cannot be undone. Sections and files in the library stay.',
    deleteTalks_one: 'The report of {{count}} talk is deleted too.',
    deleteTalks_few: 'The reports of {{count}} talks are deleted too.',
    deleteTalks_other: 'The reports of {{count}} talks are deleted too.',
    deleted: 'The presentation was deleted permanently.',
    showArchived: 'Show archived',
    updated: 'Updated {{date}}',
  },
  builder: {
    tabs: { backlog: 'Backlog', polls: 'Polls', settings: 'Settings' },
    preview: 'Preview',
    publish: 'Save',
    published: 'Version {{version}} saved.',
    notChanged: 'Nothing changed since the last save.',
    backlogTitle: 'Product Backlog',
    backlogHint:
      'This is the base order of sections. During the talk the audience picks the actual order by voting, and forward or rewind transitions are based on this order.',
    intro: 'Intro',
    introHint: 'Title and intro slides. Without them a generated title page is shown.',
    conclusion: 'Conclusion',
    conclusionHint: 'A special section shown at the end or when time runs out. It is never offered in polls.',
    setConclusion: 'Choose conclusion',
    replaceConclusion: 'Replace',
    addTopic: 'Add section',
    addTopicTitle: 'Add a section from the library',
    noTopicsLeft: 'Every library section is already added.',
    createTopicHint: 'Create a new section in the library.',
    editSlides: 'Slides',
    resetSlides: 'Reset slides from library',
    removeSection: 'Remove from presentation',
    sectionSettings: 'Section settings',
    titleOverride: 'Title in this presentation',
    titleOverrideHint: 'Leave empty to use the library title: {{title}}',
    durationOverride: 'Duration in this presentation (min)',
    position: 'Position {{position}}',
    estimated: 'Estimate: {{estimated}} of {{total}} min',
    slidesEditorTitle: 'Slides: {{title}}',
    slidesEditorHint: 'This selection applies to this presentation only. The library stays unchanged.',
    noSlides: 'No slides',
    slidesFor: 'Slides: {{title}} ({{count}})',
    archivedTopic: 'Section is archived',
    conflict: 'The presentation was changed in another window. The latest version is loaded.',
  },
  polls: {
    title: 'Polls',
    subtitle: 'Every poll is Sprint Planning. Answers lead to sections in the backlog.',
    dynamicTitle: 'Dynamic poll',
    dynamicEnabled: 'After every section',
    dynamicOffBody:
      'Off. After a section only the prepared poll suggested for it opens; without one, the next section in the backlog starts on its own, and the Conclusion follows the last one.',
    dynamicBody:
      'Automatically offers every section the audience has not heard yet, in backlog order. Used when no prepared poll is chosen.',
    dynamicDefaultQuestion: 'What should we talk about in the next Sprint?',
    staticTitle: 'Prepared polls',
    create: 'New poll',
    edit: 'Edit poll',
    question: 'Question',
    options: 'Answers',
    option: 'Answer {{index}}',
    optionLabel: 'Answer text',
    target: 'Leads to section',
    chooseTarget: 'Choose a section',
    addOption: 'Add answer',
    initial: 'Initial poll',
    initialHint: 'Opens after the intro.',
    suggestAfter: 'Suggest after section',
    suggestAfterNone: 'No suggestion',
    duration: 'Close automatically (seconds)',
    durationHint: 'Leave empty to close the poll manually.',
    emptyTitle: 'No prepared polls',
    emptyBody: 'The dynamic poll covers every case; a prepared poll uses your own question and answers.',
    sameTargetHint: 'Several answers may lead to the same section; their votes are added up.',
    previewTitle: 'What the audience sees',
    previewHint: 'Tick sections that were already presented to see which answers remain.',
    previewInsufficient:
      'Fewer than two answers remain. No vote is started; the presenter picks a section or the conclusion.',
    deleteTitle: 'Delete poll?',
    deleteBody: 'The poll and its answers will be removed from the draft.',
    unlinked: 'Answer is not linked to a section',
    initialBadge: 'Initial',
    afterBadge: 'After: {{topic}}',
    autoClose: 'Closes after {{seconds}} s',
  },
  settings: {
    timeboxTitle: 'Timebox',
    duration: 'Talk duration (minutes)',
    warnings: 'Warnings before the end (seconds, comma separated)',
    repeatTitle: 'Section repetition',
    repeat: {
      NEVER: 'Presented sections are not offered again',
      ALWAYS: 'Presented sections may be offered again',
      PRESENTER_CONTROLLED: 'The presenter enables repeats during the talk',
    },
    repeatHint: {
      NEVER: 'The usual choice: every section is heard once.',
      ALWAYS: 'The audience may ask for the same section again.',
      PRESENTER_CONTROLLED: 'Behaves like the first rule, with manual exceptions.',
    },
    transitionsTitle: 'Transition animation',
    transitionsEnabled: 'Show the slide fast-forward',
    transitionSpeed: 'Transition length: {{seconds}} s',
    flowTitle: 'Talk flow',
    afterTopicEnd: 'After a section ends',
    afterTopicEndOptions: {
      AUTO_OPEN_SUGGESTED_POLL: 'Prepare the suggested poll',
      SHOW_BACKLOG: 'Show the backlog to the presenter',
    },
    resultsVisibility: 'Results on the projector',
    resultsOptions: { LIVE: 'Live while voting', AFTER_CLOSE: 'After closing', HIDDEN: 'Do not show' },
    audienceSeesResults: 'Participants see results on their phones',
    sprintReviewTitle: 'Conclusion',
    sprintReviewEnabled: 'Slide "What we delivered" in the Conclusion',
    sprintReviewHint:
      'The app builds the slide itself: delivered sections with a check mark, started ones marked, and below them the ones left in the backlog.',
    sprintReviewPosition: 'Place in the Conclusion',
    sprintReviewFirst: 'At the start of the Conclusion',
    sprintReviewAfter: 'After slide {{position}} of the Conclusion',
    engagementTitle: 'Audience interaction',
    reactionsEnabled: 'Slide reactions',
    questionsEnabled: 'Anonymous questions',
    saved: 'Settings saved.',
  },
  validation: {
    title: 'Ready to save',
    ready: 'Ready to save',
    notReady_one: '{{count}} problem blocks saving',
    notReady_few: '{{count}} problems block saving',
    notReady_other: '{{count}} problems block saving',
    warnings: 'Warnings',
    codes: {
      NO_CONCLUSION: 'Choose a Conclusion section.',
      NO_REGULAR_TOPICS: 'Add at least one section to the backlog.',
      SECTION_NO_SLIDES: 'Section "{{section}}" has no slides.',
      SLIDE_ARCHIVED: 'Section "{{section}}" uses a slide from an archived file.',
      TOPIC_ARCHIVED: 'Section "{{section}}" is archived in the library.',
      OPTION_UNLINKED: 'Poll "{{poll}}" has an answer without a section.',
      OPTION_TARGET_NOT_VOTEABLE: 'Poll "{{poll}}" leads to a section that cannot be voted for.',
      POLL_LT_2_TARGETS: 'Poll "{{poll}}" must lead to at least two different sections.',
      POLL_NO_QUESTION: 'A poll has no question.',
      INVALID_DURATION: 'The talk duration is not valid.',
      DURATION_EXCEEDED: 'The estimated section time exceeds the timebox.',
      SINGLE_REGULAR_TOPIC: 'With a single section there is nothing to vote on.',
      SURVEY_NO_QUESTIONS: 'The survey "{{survey}}" has no questions.',
      SURVEY_FEW_OPTIONS: 'A question in the survey "{{survey}}" has fewer than two options.',
      SURVEY_QUIZ_NO_CORRECT: 'A quiz in the survey "{{survey}}" has no correct answer.',
      SURVEY_PLACEMENT_MISSING: 'The survey "{{survey}}" follows a slide that no longer exists.',
      SURVEY_PLACEMENT_DUPLICATE: 'The survey "{{survey}}" is in the same place as another survey.',
    },
  },
  preview: {
    title: 'Presentation preview',
    draft: 'Draft',
    revision: 'Version {{version}}',
    keyboardHint: 'Left and right arrows change the slide. Escape closes the preview.',
    exit: 'Close preview',
    slideOf: 'Slide {{current}} of {{total}}',
    generatedTitle: 'Generated title page',
  },
  live: {
    start: 'Start the talk',
    startHint: 'The talk uses saved version {{version}}.',
    startNeedsPublish: 'Save the presentation first.',
    resumeSession: 'Resume the talk',
    console: 'Presenter console',
    joinCode: 'Code',
    joinAt: 'Join at',
    connectAt: 'Join at {{address}}',
    scanToJoin: 'Scan the QR code or open {{url}} and enter the code',
    participants_one: '{{count}} participant',
    participants_few: '{{count}} participants',
    participants_other: '{{count}} participants',
    votes_one: '{{count}} vote',
    votes_few: '{{count}} votes',
    votes_other: '{{count}} votes',
    connection: {
      connected: 'Connected',
      connecting: 'Connecting…',
      reconnecting: 'Reconnecting…',
      offline: 'Offline',
    },
    timeSetup: {
      title: 'How long is the talk?',
      hint: 'Drag around the ring or pick a length. The countdown starts when you click Start the clock.',
      projectorTitle: 'Time for the talk',
      projectorHint: 'The presenter is setting the length',
      dial: 'Length of the talk',
      minutes_one: '{{count}} minute',
      minutes_few: '{{count}} minutes',
      minutes_other: '{{count}} minutes',
      minutesShort_one: '{{count}} min',
      minutesShort_few: '{{count}} min',
      minutesShort_other: '{{count}} min',
      less: 'Five minutes less',
      more: 'Five minutes more',
      go: 'Start the clock',
      waiting: 'The presenter is setting the time. We start in a moment.',
    },
    sprintReview: {
      eyebrow: 'Sprint Review',
      title: 'What we delivered',
      started: 'started',
      deliveredItem: 'delivered',
      backlog: 'Still in the backlog',
      nothingDelivered: 'No section has been covered yet.',
      allDelivered: 'Every section in the backlog was delivered.',
    },
    joinCodePicker: {
      title: 'Choose a code',
      open: 'Change the code',
      warning:
        'After the change the old link and QR code stop working. Anyone already connected stays connected.',
      suggestions: 'Suggestions',
      generate: 'Generate new ones',
      custom: 'The code you want',
      customHint: 'Six characters: letters without I and O, digits 2 to 9.',
      apply: 'Use this code',
      changed: 'The new code is {{code}}.',
      taken: 'Another talk already uses this code. Choose a different one.',
      completed: 'A finished talk keeps its code.',
      failed: 'The code was not changed. Try again.',
      loadFailed: 'The suggestions did not load.',
    },
    joinOverlay: {
      show: 'Show the code on the projector',
      hide: 'Hide the code from the projector',
    },
    modes: {
      NOT_STARTED: 'Ready to start',
      INTRO: 'Intro',
      TIME_SETUP: 'Setting the time',
      BACKLOG: 'Backlog',
      POLL: 'Voting',
      RESULTS: 'Results',
      TOPIC: 'Sprint in progress',
      CONCLUSION: 'Conclusion',
      COMPLETED: 'The talk has ended',
    },
    startSession: 'Start',
    endSession: 'End the talk',
    endSessionConfirm: 'End the talk? Participants will no longer be able to vote.',
    openProjector: 'Open projector',
    projectorElsewhere: 'Projector on another computer',
    remote: 'Control from your phone',
    remoteTitle: 'Phone remote',
    remoteBody: 'Scan the code with your phone. It is valid for two minutes and works once.',
    projectorPairBody: 'Open this address on the projector computer. Valid for two minutes, once.',
    pairingExpires: 'Expires in {{seconds}} s',
    newCode: 'New code',
    devices: 'Paired devices',
    noDevices: 'No paired devices.',
    revoke: 'Sign out',
    online: 'online',
    offlineDevice: 'offline',
    previous: 'Previous',
    next: 'Next',
    slideOf: '{{current}} / {{total}}',
    nextSlide: 'Next slide',
    lastSlide: 'Last slide of the section',
    endTopic: 'End section',
    conclusion: 'Conclusion',
    goConclusion: 'Go to the Conclusion',
    backlog: 'Product Backlog',
    status: { NOT_PRESENTED: 'Not presented', IN_PROGRESS: 'In progress', COMPLETED: 'Completed' },
    presentedTimes: 'presented {{count}}×',
    audienceValue: 'Value for the audience',
    audienceValueHint:
      'Share of votes in the latest poll that offered the section, renormalised over the sections still available.',
    noValueYet: 'no votes yet',
    staleValue: 'from an earlier poll',
    pollPanel: 'Sprint Planning',
    choosePoll: 'Choose a poll',
    suggested: 'Suggested',
    openPoll: 'Open poll',
    replacePoll: 'Replace poll',
    closePoll: 'Close voting',
    cancelPoll: 'Cancel poll',
    repoll: 'Vote again',
    inOrder: 'Sections follow the backlog order, and the Conclusion starts after the last one.',
    insufficient: 'Voting needs at least two available sections.',
    waitingForVotes: 'Waiting for the first votes',
    winner: 'Winner: {{topic}}',
    tie: 'Tie: choose one of the tied sections',
    noVotes: 'Nobody voted. Repeat the poll or choose a section.',
    confirm: 'Accept: {{topic}}',
    override: 'Choose another section',
    chooseTopic: 'Choose a section',
    backlogReasons: {
      AFTER_TOPIC: 'The section has ended. Choose a poll or the next section.',
      INSUFFICIENT_OPTIONS: 'Only one section is left. Go to it or to the Conclusion.',
      NO_ELIGIBLE: 'Every section has been presented. Time for the Conclusion.',
      POLL_CANCELLED: 'The poll was cancelled.',
    },
    goTo: 'Go',
    alreadyPresented: 'This section was already presented and repeats are off. Go to it anyway?',
    forceGo: 'Go anyway',
    dynamicQuestion: 'What should we talk about in the next Sprint?',
    history: 'Talk flow',
    entry: {
      START: 'start',
      VOTE_WINNER: 'vote',
      TIE_BREAK: 'tie break',
      OVERRIDE: 'override',
      MANUAL_NO_VOTES: 'manual, no votes',
      DIRECT: 'presenter choice',
      IN_ORDER: 'in order',
      CONCLUSION_MANUAL: 'conclusion',
      CONCLUSION_TIMER: 'conclusion when time ran out',
      RESUME: 'resumed after the conclusion',
    },
    keyboard: 'Arrow keys change the slide, Z goes to the Conclusion.',
    timer: {
      title: 'Time',
      open: 'Time controls',
      reduceBy: 'Take away {{minutes}} min',
      remaining: 'Time left',
      paused: 'Paused',
      expired: 'Time is up',
      planned: '{{minutes}} min planned',
      pause: 'Pause',
      resume: 'Resume',
      set: 'Set',
      setTitle: 'Time left',
      setBody: 'Enter how many minutes are left until the end of the talk.',
      minutes: 'min',
      duration: 'Duration',
      warning: '{{time}} left.',
      expiredHint: 'The Conclusion has started. You add time when you resume the talk.',
    },
    resume: {
      timerTitle: 'Time is up, the Conclusion has started',
      timerBody: 'The clock stays at 00:00. Finish the Conclusion or add time and resume the talk.',
      manualTitle: 'Conclusion',
      manualBody: 'You can go back to the talk without adding time.',
      addTime: 'Extra time',
      noExtra: 'No extra time',
      previous: 'Back to: {{topic}}',
      previousAt: 'Back to: {{topic}}, slide {{slide}}',
      poll: 'New poll',
      backlog: 'Backlog',
    },
    repeat: {
      allow: 'Allow this section again',
      allowed: 'Repeat allowed',
    },
    completed: {
      duration: '{{planned}} min planned, {{actual}} min held.',
      abandoned: 'The talk ended automatically after 12 hours without activity. ',
      reopen: 'Reopen ({{time}})',
    },
    reactions: {
      title: 'Reactions',
      slide: 'This slide',
      topic: 'This section',
      session: 'Total',
    },
    questions: {
      title: 'Audience questions',
      newCount_one: '{{count}} new',
      newCount_few: '{{count}} new',
      newCount_other: '{{count}} new',
      filter: 'Question view',
      filters: { NEW: 'New', ANSWERED: 'Answered', HIDDEN: 'Hidden', ALL: 'All' },
      empty: 'No questions in this view.',
      project: 'Show on projector',
      unproject: 'Remove from projector',
      answer: 'Answered',
      hide: 'Hide',
      restore: 'Restore',
      onProjector: 'on projector',
      fromAudience: 'Question from the audience',
    },
    overview: {
      title: 'Backlog overview',
      columns: { NOT_PRESENTED: 'Not presented', IN_PROGRESS: 'In progress', COMPLETED: 'Completed' },
      empty: 'No sections',
      planned: 'Planned order',
      actual: 'Actual order',
      plannedAt: 'plan #{{position}}',
      interrupted: 'interrupted',
      nothingPresented: 'No section was presented.',
    },
    contents: 'Contents',
    controls: 'Controls',
    paired: 'Phone paired.',
    pairFailed: 'The pairing code expired or was already used. Ask for a new one in the console.',
    pairing: 'Pairing…',
    completedTopics: 'Done',
    backToSlides: 'Back to the slides',
    backToSlidesTitle: 'Go back to the slides?',
    backToSlidesBody:
      'The poll will be cancelled and the votes received ({{votes}}) discarded. When you move on again, a new poll opens.',
    questionsTab: 'Questions',
    newQuestions: 'New questions',
    remoteEnded: 'The talk has ended.',
    commandFailed: 'Command not executed: {{code}}',
    rejections: {
      NOT_ENOUGH_TIME: 'At least one minute has to stay. To end the talk, go to the Conclusion.',
      NOTHING_TO_RETURN_TO:
        'The poll was not opened right after a section, so there is no slide to go back to.',
      MODE_NOT_ALLOWED: 'This command is not available right now.',
      POLL_NOT_OPEN: 'Voting is no longer open.',
      STALE_NAVIGATION: 'The slide changed in the meantime.',
      STALE_STATE: 'The state changed in the meantime.',
      INSUFFICIENT_OPTIONS: 'Not enough sections are available for a vote.',
      NOT_ELIGIBLE: 'The section is not available.',
      END_OF_CONCLUSION: 'This is the last slide of the Conclusion.',
      AT_FIRST_SLIDE: 'This is the first slide.',
      POLL_ALREADY_OPEN: 'A poll is already open.',
      INVALID_DURATION: 'The duration must be between 1 and 360 minutes.',
      TIMER_EXPIRED_USE_RESUME: 'Time is up. To continue, choose an option in the Conclusion and add time.',
      RESUME_NEEDS_TIME: 'Time is up. Add time to continue.',
      NOTHING_TO_RESUME: 'There is no section to return to.',
      REPEAT_NOT_CONTROLLED: 'The repeat rule of this presentation does not allow manual repeats.',
      REOPEN_WINDOW_EXPIRED: 'A talk can be reopened only within 15 minutes after it ends.',
      ANOTHER_SESSION_ACTIVE: 'Another talk is already running for this presentation.',
      QUESTION_HIDDEN: 'A hidden question cannot be shown on the projector.',
      QUESTION_NOT_FOUND: 'The question does not exist.',
    },
    keepAwake: 'The screen stays on while the remote is open.',
  },
  audience: {
    joinTitle: 'Join the talk',
    joinBody: 'Enter the code shown on the projector.',
    codeLabel: 'Talk code',
    join: 'Join',
    notFound: 'There is no talk with that code.',
    ended: 'The talk has ended. Thank you for taking part!',
    waiting: 'The talk will start soon.',
    voteTitle: 'Your vote',
    voted: 'Vote received. You can change it while voting is open.',
    closed: 'Voting is closed.',
    results: 'Results',
    nowPresenting: 'Now',
    resultsHidden: 'Results will be shown on the projector.',
    anonymous: 'Voting is anonymous.',
    reactTitle: 'Reactions',
    reactions: {
      LIKE: 'Like',
      INTERESTING: 'Interesting',
      UNCLEAR: 'Not clear',
      MORE_DETAILS: 'More details',
    },
    askTitle: 'Question for the presenter',
    askPlaceholder: 'Write a question (up to 300 characters)',
    askSend: 'Send',
    askSent: 'Your question was sent.',
    askAnonymous: 'Questions are anonymous. The presenter decides whether one is shown on the projector.',
    editQuestion: 'Edit',
    editQuestionLabel: 'Edit the question',
    edited: 'edited',
    questionStatus: { NEW: 'sent', ANSWERED: 'answered', HIDDEN: 'hidden' },
    engagementErrors: {
      QUESTION_LOCKED:
        'The question can no longer be changed because it was answered or is on the projector.',
      QUESTION_NOT_FOUND: 'The question was not found.',
      RATE_LIMITED: 'A little slower, please try again in a moment.',
      DUPLICATE: 'You already sent the same question.',
      SPAM: 'This looks like spam.',
      TOO_SHORT: 'The question is too short.',
      TOO_LONG: 'The question is too long.',
      TOO_MANY_QUESTIONS: 'You have sent the maximum number of questions for this talk.',
      VALIDATION_FAILED: 'A question needs 3 to 300 characters.',
      NO_SLIDE: 'Reactions are possible while a slide is shown.',
      REACTIONS_DISABLED: 'Reactions are turned off.',
      QUESTIONS_DISABLED: 'Questions are turned off.',
      SESSION_NOT_LIVE: 'The talk is not running.',
      OFFLINE: 'No connection to the server.',
      GENERIC: 'Sending failed. Please try again.',
    },
  },
  collections: {
    label: 'Area',
    presentationLabel: 'Area of this presentation',
    presentationHint: 'The builder and the slide picker offer sections and files from this area first.',
    filter: 'Filter by area',
    all: 'All',
    none: 'No area',
    create: 'New area',
    createTitle: 'New area',
    editTitle: 'Edit area',
    edit: 'Rename or recolour',
    description:
      'An area groups the sections, files and presentations of one course or subject, for example "Algorithms and programming".',
    name: 'Name',
    namePlaceholder: 'Algorithms and programming',
    color: 'Colour',
    saved: 'The area was saved.',
    manage: 'Settings of the area {{name}}',
    delete: 'Delete area',
    deleteTitle: 'Delete the area "{{name}}"?',
    deleteBody: 'Sections, files and presentations stay; they just no longer belong to an area.',
    deleted: 'The area was deleted.',
    moveTitle: 'Move to area',
    move: 'Move',
    moved: 'Moved.',
    actionsFor: 'Actions for {{title}}',
  },
  reports: {
    historyTitle: 'Past talks',
    historySubtitle: 'Every run of a presentation, with its report and export.',
    emptyTitle: 'No talks yet',
    emptyBody: 'Start a saved presentation and its report will appear here.',
    open: 'Report',
    delete: 'Delete report',
    deleteConfirm:
      'Delete the report of "{{title}}"? Votes, reactions and questions of this talk are deleted.',
    deleted: 'The report was deleted.',
    status: { CREATED: 'not started', LIVE: 'running', COMPLETED: 'completed' },
    abandoned: 'ended automatically after inactivity',
    exportCsv: 'Export CSV',
    exportPdf: 'Export PDF',
    duration: 'Duration',
    plannedMinutes: '{{minutes}} min planned',
    uniqueParticipants: 'Unique participants',
    peak: 'peak {{count}} at once',
    reactions: 'Reactions',
    questions: 'Questions',
    questionsHint: '{{answered}} answered, {{hidden}} hidden',
    participantsOverTime: 'Connected participants over time',
    noSamples: 'No data about connected participants.',
    polls: 'Polls',
    pollState: { OPEN: 'open', CLOSED: 'closed', RESOLVED: 'decided', CANCELLED: 'cancelled' },
    none: 'None.',
    actualOrder: 'Actual section order',
    topic: 'Section',
    plannedPosition: 'Plan',
    entry: 'Entry',
    visitStatus: 'Status',
    visitStatuses: { IN_PROGRESS: 'running', COMPLETED: 'completed', INTERRUPTED: 'interrupted' },
    start: 'Start',
    minutes: 'Minutes',
    topics: 'Sections, time and reactions',
    visits: 'Visits',
    notPresented: 'Sections not presented',
    decisions: 'Manual decisions',
    slideReactions: 'Reactions per slide',
    slide: 'Slide',
    winnerWas: 'vote winner: {{topics}}',
  },
  surveys: {
    tab: 'Surveys',
    intro:
      'Surveys collect audience impressions and do not choose the next section. A placed survey opens when the presenter moves past the chosen slide; others are started from the console.',
    add: 'New survey',
    addTemplate: 'Add satisfaction template',
    empty: 'No surveys yet.',
    edit: 'Edit survey',
    create: 'New survey',
    delete: 'Delete survey',
    deleteConfirm: 'Delete the survey "{{title}}"?',
    save: 'Save survey',
    titleLabel: 'Survey title',
    placement: 'When it shows',
    placementManual: 'Manually, from the console',
    placementEnd: 'At the end of the conclusion, after the last slide',
    placementSlide: 'After a chosen slide',
    section: 'Section',
    slide: 'Slide',
    slideNumber: 'Slide {{number}}',
    afterSlide: 'After slide {{number}}, {{section}}',
    atEnd: 'At the end of the conclusion',
    manual: 'Started manually',
    missingPlacement: 'The slide for this survey no longer exists. Choose a new place.',
    questions: 'Questions',
    addQuestion: 'Add question',
    prompt: 'Question',
    options: 'Answer options',
    addOption: 'Add option',
    optionLabel: 'Option {{number}}',
    multiple: 'Allow several answers',
    correct: 'Correct',
    ratingMax: 'Scale up to',
    ratingStyle: 'Display',
    stars: 'Stars',
    numbers: 'Numbers',
    maxWords: 'Words per participant',
    moveUp: 'Move up',
    moveDown: 'Move down',
    removeQuestion: 'Remove question',
    removeOption: 'Remove option',
    questionCount_one: '{{count}} question',
    questionCount_few: '{{count}} questions',
    questionCount_other: '{{count}} questions',
    types: {
      CHOICE: 'Multiple choice',
      RATING: 'Rating',
      WORD_CLOUD: 'Word cloud',
      OPEN_TEXT: 'Open text',
      QUIZ: 'Quiz',
      RANKING: 'Ranking',
    },
    typeHints: {
      CHOICE: 'One or more options, results as bars.',
      RATING: 'A rating on a scale, with the average.',
      WORD_CLOUD: 'Repeated words grow bigger.',
      OPEN_TEXT: 'Free text; the presenter can hide an answer.',
      QUIZ: 'A question with a correct answer you reveal when you want.',
      RANKING: 'Participants order the options by importance.',
    },
    card: 'Surveys',
    launch: 'Start',
    showAgain: 'Show again',
    quick: 'Quick survey',
    quickTitle: 'Quick survey',
    satisfaction: 'Talk satisfaction',
    prepared: 'Prepared surveys',
    inline: 'placed',
    ran: 'done',
    running: 'Survey running',
    closedLabel: 'Closed',
    onProjector: 'On the projector',
    resultsOnPhones: 'Results on phones',
    reveal: 'Reveal the correct answer',
    revealed: 'The correct answer is revealed',
    close: 'Close survey',
    reopen: 'Open again',
    hideAnswer: 'Hide',
    restoreAnswer: 'Restore',
    previousQuestion: 'Previous question',
    nextQuestion: 'Next question',
    questionOf: 'Question {{current}} of {{total}}',
    responses_one: '{{count}} response',
    responses_few: '{{count}} responses',
    responses_other: '{{count}} responses',
    respondents_one: '{{count}} participant',
    respondents_few: '{{count}} participants',
    respondents_other: '{{count}} participants',
    average: 'Average',
    points: 'points',
    averagePosition: 'average position {{value}}',
    noResponses: 'No responses yet.',
    inlineHint: 'The next slide closes the survey and continues the presentation.',
    projectedNow: 'The survey is on the projector',
    send: 'Send',
    change: 'Change answer',
    sent: 'Your answer was sent.',
    next: 'Next',
    previous: 'Previous',
    wordPlaceholder: 'Type a word',
    resultPage: 'Page {{page}} of {{pages}}',
    textPlaceholder: 'Your answer',
    rankHint: 'Order from most to least important.',
    up: 'Move {{label}} up',
    down: 'Move {{label}} down',
    closedNotice: 'The survey is closed.',
    allAnswered: 'Thank you! You answered every question.',
    correctMark: 'correct',
    conclusionPrompt: 'Start the satisfaction survey before the talk ends.',
    endWarning: 'The survey "{{title}}" is still open and will be closed.',
    history: 'Surveys in this talk',
    nextSurvey: 'Next: survey "{{title}}"',
    continueAfterSurvey: 'Close the survey and continue',
    showSurveyAgain: 'Show the survey again',
    launchFailed: 'The survey did not start.',
    wordCloudEmpty: 'Words appear when the audience answers.',
  },
  admin: {
    title: 'Users',
    subtitle: 'Account approval, roles, passwords and deletion.',
    add: 'Add user',
    filter: 'Show',
    filters: { PENDING: 'Pending', ACTIVE: 'Active', DISABLED: 'Disabled', ALL: 'All' },
    status: { PENDING: 'Pending', ACTIVE: 'Active', DISABLED: 'Disabled' },
    role: { PRESENTER: 'Presenter', ADMIN: 'Administrator' },
    columns: {
      user: 'User',
      role: 'Role',
      status: 'Status',
      created: 'Registered',
      lastSeen: 'Last active',
      content: 'Content',
      actions: 'Actions',
    },
    content: '{{presentations}} pres. / {{assets}} files',
    never: 'never',
    you: 'you',
    live: 'presenting now',
    empty: 'No users in this view.',
    pendingNotice_one: '{{count}} account is waiting for approval.',
    pendingNotice_few: '{{count}} accounts are waiting for approval.',
    pendingNotice_other: '{{count}} accounts are waiting for approval.',
    actionsFor: 'Actions for {{name}}',
    approve: 'Approve',
    reject: 'Reject',
    edit: 'Edit',
    setPassword: 'New password',
    disable: 'Disable',
    enable: 'Enable',
    delete: 'Delete',
    save: 'Save',
    create: 'Create account',
    generate: 'Generate',
    approved: '{{email}} is approved.',
    rejected: 'The request from {{email}} was rejected.',
    saved: 'Saved.',
    created: '{{email}} was created.',
    passwordSet: 'Password changed. The user was signed out everywhere.',
    disabled: '{{email}} is disabled.',
    enabled: '{{email}} is enabled.',
    deleted: '{{email}} was deleted.',
    editTitle: 'Edit account',
    createTitle: 'New user',
    passwordTitle: 'New password for {{name}}',
    passwordBody: 'The user is signed out on every device. Share the password with them yourself.',
    disableTitle: 'Disable this account?',
    disableBody:
      '{{name}} will not be able to sign in, and their content stays. You can enable the account again.',
    rejectTitle: 'Reject this request?',
    rejectBody: 'The account request from {{email}} is deleted. The person can register again.',
    deleteTitle: 'Delete this account permanently?',
    deleteBody:
      'This deletes {{email}} and everything it owns: {{presentations}} presentations, {{assets}} files, sections, areas and talk reports. This cannot be undone.',
    deleteConfirmLabel: 'Type the account email address to confirm',
  },
  notFound: { title: 'Page not found', body: 'Check the address or go back home.', home: 'Go home' },
};
